/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  TextField,
  Modal,
  ModalFooter,
  Selection,
} from '@folio/stripes-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { getVolumePrefix, getVolumeNumber } from '../utils/volumeUtils';

// Enhanced Reset Volume Modal component with prefix editing
const ResetVolumeModal = ({ open, onClose, onReset, volume, volumeItems }) => {
  const intl = useIntl();
  // All hooks must be at the beginning before any early return
  const [selectedPrefix, setSelectedPrefix] = React.useState('مج.');
  const [customPrefix, setCustomPrefix] = React.useState('');
  const [isCustomPrefix, setIsCustomPrefix] = React.useState(false);
  const [volumeNumber, setVolumeNumber] = React.useState('');

  // Predefined prefixes
  const prefixOptions = [
    { value: 'مج.', label: 'مج.' },
    { value: 'VOL.', label: 'VOL.' },
    { value: 'Vol.', label: 'Vol.' },
    { value: 'v.', label: 'v.' },
    { value: 'custom', label: 'Custom' },
  ];

  // Initialize when modal opens
  React.useEffect(() => {
    if (open && volume) {
      const detectedPrefix = getVolumePrefix(volume);
      const detectedNumber = getVolumeNumber(volume);

      // Set the detected number
      setVolumeNumber(
        detectedNumber !== 'No_Volume' ? detectedNumber.toString() : '1',
      );

      // Try to match detected prefix with predefined options
      if (
        detectedPrefix &&
        prefixOptions.some((option) => option.value === detectedPrefix)
      ) {
        setSelectedPrefix(detectedPrefix);
        setIsCustomPrefix(false);
        setCustomPrefix('');
      } else if (detectedPrefix) {
        // Use custom prefix if detected but not in predefined list
        setSelectedPrefix('custom');
        setCustomPrefix(detectedPrefix);
        setIsCustomPrefix(true);
      } else {
        // Default to مج. if no prefix detected
        setSelectedPrefix('مج.');
        setIsCustomPrefix(false);
        setCustomPrefix('');
      }
    }
  }, [open, volume]);

  // Reset form when modal closes
  React.useEffect(() => {
    if (!open) {
      setSelectedPrefix('مج.');
      setCustomPrefix('');
      setIsCustomPrefix(false);
      setVolumeNumber('');
    }
  }, [open]);

  // Early return AFTER all hooks have been called - this is the main fix
  if (!volume) return null;

  const handlePrefixChange = (value) => {
    if (value === 'custom') {
      setIsCustomPrefix(true);
      setSelectedPrefix('custom');
    } else {
      setIsCustomPrefix(false);
      setSelectedPrefix(value);
      setCustomPrefix('');
    }
  };

  const handleCustomPrefixChange = (e) => {
    setCustomPrefix(e.target.value);
  };

  const getNewVolumeString = () => {
    const prefix = isCustomPrefix ? customPrefix : selectedPrefix;
    return volumeNumber ? `${prefix}${volumeNumber}` : '';
  };

  const handleReset = () => {
    const newVolumeString = getNewVolumeString();
    // Pass both parameters: current volume and new volume
    onReset(volume, newVolumeString);
    onClose();
  };

  // Check what volume fields currently exist in the items
  const currentVolumeFields =
    volumeItems?.map((item) => item.volume || 'Empty') || [];
  const uniqueVolumeFields = [...new Set(currentVolumeFields)];
  const hasInconsistentFields = uniqueVolumeFields.length > 1;

  const isValidNumber =
    volumeNumber &&
    !Number.isNaN(parseInt(volumeNumber, 10)) &&
    parseInt(volumeNumber, 10) > 0;
  const isValidPrefix = isCustomPrefix
    ? customPrefix.trim() !== ''
    : selectedPrefix !== '';

  return (
    <Modal
      open={open}
      label={
        <FormattedMessage
          id="ui-plugin-inventory-templates.volumeManagement.resetVolumeModal.label"
          defaultMessage="Reset & Standardize Volume"
        />
      }
      onClose={onClose}
      size="medium"
    >
      <div style={{ padding: '20px' }}>
        <p style={{ marginBottom: '16px' }}>
          <FormattedMessage
            id="ui-plugin-inventory-templates.volumeManagement.resetVolumeModal.description"
            values={{
              volume,
              itemCount: volumeItems?.length || 0,
            }}
            defaultMessage="Reset and standardize volume {volume} ({itemCount} items)"
          />
        </p>

        {/* Volume Prefix Selection */}
        <div style={{ marginBottom: '16px' }}>
          <Selection
            label={
              <FormattedMessage
                id="ui-plugin-inventory-templates.volumeManagement.resetVolumeModal.volumePrefix.label"
                defaultMessage="Volume Prefix"
              />
            }
            placeholder={intl.formatMessage({
              id: 'ui-plugin-inventory-templates.volumeManagement.resetVolumeModal.volumePrefix.placeholder',
              defaultMessage: 'Select prefix',
            })}
            dataOptions={prefixOptions}
            value={isCustomPrefix ? 'custom' : selectedPrefix}
            onChange={handlePrefixChange}
          />
        </div>

        {/* Custom Prefix Input */}
        {isCustomPrefix && (
          <div style={{ marginBottom: '16px' }}>
            <TextField
              label={
                <FormattedMessage
                  id="ui-plugin-inventory-templates.volumeManagement.resetVolumeModal.customPrefix.label"
                  defaultMessage="Custom Prefix"
                />
              }
              value={customPrefix}
              onChange={handleCustomPrefixChange}
              placeholder={intl.formatMessage({
                id: 'ui-plugin-inventory-templates.volumeManagement.resetVolumeModal.customPrefix.placeholder',
                defaultMessage: 'Enter custom prefix',
              })}
              required
            />
          </div>
        )}

        {/* Volume Number Input */}
        <div style={{ marginBottom: '16px' }}>
          <TextField
            label={
              <FormattedMessage
                id="ui-plugin-inventory-templates.volumeManagement.resetVolumeModal.volumeNumber.label"
                defaultMessage="Volume Number"
              />
            }
            type="number"
            min="1"
            value={volumeNumber}
            onChange={(e) => setVolumeNumber(e.target.value)}
            placeholder={intl.formatMessage({
              id: 'ui-plugin-inventory-templates.volumeManagement.resetVolumeModal.volumeNumber.placeholder',
              defaultMessage: 'Enter volume number',
            })}
            required
          />
        </div>

        {/* Preview */}
        {getNewVolumeString() && (
          <div
            style={{
              backgroundColor: '#e8f5e8',
              padding: '12px',
              borderRadius: '6px',
              marginBottom: '16px',
              border: '1px solid #c3e6c3',
            }}
          >
            <div
              style={{
                fontWeight: '600',
                marginBottom: '4px',
                color: '#2d5a2d',
              }}
            >
              <FormattedMessage
                id="ui-plugin-inventory-templates.volumeManagement.resetVolumeModal.preview.label"
                defaultMessage="New standardized volume:"
              />
            </div>
            <div
              style={{ fontSize: '16px', fontWeight: '600', color: '#2d5a2d' }}
            >
              {getNewVolumeString()}
            </div>
          </div>
        )}

        {/* Changes Summary */}
        <div
          style={{
            backgroundColor: '#f8f9fa',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '16px',
            border: '1px solid #dee2e6',
          }}
        >
          <div
            style={{ fontWeight: '600', marginBottom: '8px', color: '#495057' }}
          >
            <FormattedMessage
              id="ui-plugin-inventory-templates.volumeManagement.resetVolumeModal.changes.title"
              defaultMessage="Changes to be made:"
            />
          </div>
          <ul style={{ margin: 0, paddingLeft: '20px', color: '#6c757d' }}>
            <li>
              <FormattedMessage
                id="ui-plugin-inventory-templates.volumeManagement.resetVolumeModal.changes.copyNumbers"
                defaultMessage="Copy numbers: 1, 2, 3, 4..."
              />
            </li>
            <li>
              <FormattedMessage
                id="ui-plugin-inventory-templates.volumeManagement.resetVolumeModal.changes.volumeField"
                values={{ newVolume: getNewVolumeString() || 'Not specified' }}
                defaultMessage="Volume field: {newVolume}"
              />
            </li>
            <li>
              <FormattedMessage
                id="ui-plugin-inventory-templates.volumeManagement.resetVolumeModal.changes.order"
                defaultMessage="Item order: Preserved"
              />
            </li>
          </ul>
        </div>

        {/* Current Inconsistencies */}
        {hasInconsistentFields && (
          <div
            style={{
              backgroundColor: '#fff3cd',
              padding: '12px',
              borderRadius: '6px',
              marginBottom: '16px',
              border: '1px solid #ffeaa7',
            }}
          >
            <div
              style={{
                fontWeight: '600',
                marginBottom: '8px',
                color: '#856404',
              }}
            >
              <FormattedMessage
                id="ui-plugin-inventory-templates.volumeManagement.resetVolumeModal.inconsistent.title"
                defaultMessage="Current inconsistent volume fields:"
              />
            </div>
            <div style={{ fontSize: '14px', color: '#856404' }}>
              {uniqueVolumeFields.map((field, index) => (
                <span key={index}>
                  "{field || 'Empty'}"
                  {index < uniqueVolumeFields.length - 1 ? ', ' : ''}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <ModalFooter>
        <Button
          buttonStyle="primary"
          onClick={handleReset}
          disabled={!isValidNumber || !isValidPrefix}
          marginBottom0
        >
          <FormattedMessage
            id="ui-plugin-inventory-templates.volumeManagement.resetVolumeModal.apply"
            defaultMessage="Apply Changes"
          />
        </Button>
        <Button onClick={onClose} marginBottom0>
          <FormattedMessage
            id="ui-plugin-inventory-templates.volumeManagement.modal.cancel"
            defaultMessage="Cancel"
          />
        </Button>
      </ModalFooter>
    </Modal>
  );
};

ResetVolumeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired, // Now expects currentVolume and newVolumeString parameters
  volume: PropTypes.string,
  volumeItems: PropTypes.array,
};

export default ResetVolumeModal;
